import React from 'react';

/**
 * Error Fallback Component
 * Displays a user-friendly error message with options to reset or refresh
 */
function ErrorFallback({ error, resetErrorBoundary }) {
  // Helper to check if error is a ResizeObserver error (harmless)
  const isResizeObserverError = (error) => {
    if (!error) return false;
    const message = String(error?.message || error?.toString() || '').toLowerCase();
    const stack = String(error?.stack || '').toLowerCase();
    const combined = `${message} ${stack}`;
    
    return (
      combined.includes('resizeobserver') ||
      combined.includes('resizeobserver loop') ||
      combined.includes('resizeobserver loop limit exceeded') ||
      combined.includes('resizeobserver loop completed') ||
      combined.includes('resizeobserver loop completed with undelivered notifications') ||
      combined.includes('resizeobserver loop limit') ||
      combined.includes('undelivered notifications') ||
      combined.includes('crbug/1173575')
    );
  };

  // If it's a harmless ResizeObserver error, don't show the fallback
  // These are common browser quirks and don't affect functionality
  if (isResizeObserverError(error)) {
    // Silently suppress - these errors are harmless
    return null;
  }

  const clearCacheAndReload = () => {
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload(true);
  };

  return (
    <div
      role="alert"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        padding: '20px',
        textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        background: '#f8f9fa',
      }}
    >
      <div
        style={{
          maxWidth: '600px',
          background: 'white',
          borderRadius: '8px',
          padding: '40px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <h1
          style={{
            color: '#e74c3c',
            marginBottom: '20px',
            fontSize: '28px',
            fontWeight: '600',
          }}
        >
          ⚠️ Something went wrong
        </h1>
        
        <p
          style={{
            marginBottom: '20px',
            color: '#666',
            fontSize: '16px',
            lineHeight: '1.6',
          }}
        >
          An unexpected error occurred. Don&apos;t worry, your data is safe.
        </p>

        {process.env.NODE_ENV === 'development' && error && (
          <details
            style={{
              marginBottom: '20px',
              textAlign: 'left',
              background: '#f8f9fa',
              padding: '15px',
              borderRadius: '4px',
              border: '1px solid #dee2e6',
            }}
          >
            <summary
              style={{
                cursor: 'pointer',
                fontWeight: '600',
                color: '#495057',
                marginBottom: '10px',
              }}
            >
              Error Details (Development Only)
            </summary>
            <pre
              style={{
                overflow: 'auto',
                fontSize: '12px',
                color: '#dc3545',
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}

        <div
          style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {resetErrorBoundary && (
            <button
              onClick={resetErrorBoundary}
              style={{
                padding: '12px 24px',
                background: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'background-color 0.2s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = '#2980b9')}
              onMouseOut={(e) => (e.currentTarget.style.background = '#3498db')}
              onFocus={(e) => (e.currentTarget.style.background = '#2980b9')}
              onBlur={(e) => (e.currentTarget.style.background = '#3498db')}
            >
              Try Again
            </button>
          )}

          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              background: '#2ecc71',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500',
              transition: 'background-color 0.2s',
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = '#27ae60')}
            onMouseOut={(e) => (e.currentTarget.style.background = '#2ecc71')}
            onFocus={(e) => (e.currentTarget.style.background = '#27ae60')}
            onBlur={(e) => (e.currentTarget.style.background = '#2ecc71')}
          >
            Refresh Page
          </button>

          <button
            onClick={clearCacheAndReload}
            style={{
              padding: '12px 24px',
              background: '#95a5a6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500',
              transition: 'background-color 0.2s',
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = '#7f8c8d')}
            onMouseOut={(e) => (e.currentTarget.style.background = '#95a5a6')}
            onFocus={(e) => (e.currentTarget.style.background = '#7f8c8d')}
            onBlur={(e) => (e.currentTarget.style.background = '#95a5a6')}
          >
            Clear Cache & Reload
          </button>
        </div>

        <p
          style={{
            marginTop: '30px',
            fontSize: '14px',
            color: '#999',
          }}
        >
          If the problem persists, please contact support.
        </p>
      </div>
    </div>
  );
}

export default ErrorFallback;
